import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { STAGES, formatINR, formatDate, inputCls, labelCls, btnPrimary } from "@/lib/constants";
import { StatusBadge } from "@/components/StatusBadge";
import { Modal } from "@/components/Modal";
import { LeadDetail } from "@/components/LeadDetail";
import { LeadFormModal } from "@/components/LeadFormModal";
import { useAuth, useCanAdmin } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Search, Eye, Trash2, Columns3, ChevronUp, ChevronDown, Pencil } from "lucide-react";
import { BASE_COLS, colDef, buildCfLabels, pddDays } from "@/lib/leadColumns";

export default function Leads() {
  const { user } = useAuth();
  const canAdmin = useCanAdmin();
  const isAdmin = user?.role === "admin";
  const canManageLeads = ["admin", "sales_manager", "finance"].includes(user?.role) || canAdmin("leads");
  const canEditLead = canAdmin("leads") || user?.role === "finance";
  const [leads, setLeads] = useState([]);
  const [lenders, setLenders] = useState([]);
  const [partners, setPartners] = useState([]);
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [editLead, setEditLead] = useState(null);
  const [detailLead, setDetailLead] = useState(null);
  const [settings, setSettings] = useState({ columns: [], partner_columns: [], doc_types: [] });
  const [showCols, setShowCols] = useState(false);
  const [draftCols, setDraftCols] = useState([]);
  const [draftPartnerCols, setDraftPartnerCols] = useState([]);
  const [presetTab, setPresetTab] = useState("staff");
  const [pddMin, setPddMin] = useState("");
  const [sortPdd, setSortPdd] = useState(false);
  const [draftDocs, setDraftDocs] = useState("");
  const [draftStages, setDraftStages] = useState([]);
  const [newStage, setNewStage] = useState("");
  const CORE_STAGES = ["Lead Created", "Login Done", "Ops Approval", "Disbursed", "Sent for PDD Approval", "PDD Confirmed", "Rejected"];

  const load = useCallback(() => {
    const params = {};
    if (search) params.search = search;
    if (statusFilter) params.status = statusFilter;
    if (typeFilter) params.loan_type = typeFilter;
    api.get("/leads", { params }).then((r) => setLeads(r.data)).catch(() => {});
  }, [search, statusFilter, typeFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/lenders").then((r) => setLenders(r.data));
    api.get("/partners").then((r) => setPartners(r.data));
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/settings/lead").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const cfLabels = buildCfLabels(products);
  const cd = (key) => colDef(key, cfLabels);
  const activeCols = (settings.columns || []).filter((k) => cd(k));

  const del = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    try { await api.delete(`/leads/${id}`); toast.success("Lead deleted"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const openCols = () => {
    setDraftCols(settings.columns || []);
    setDraftPartnerCols(settings.partner_columns || settings.columns || []);
    setPresetTab("staff");
    setDraftDocs((settings.doc_types || []).join(", "));
    setDraftStages(settings.stages && settings.stages.length ? settings.stages : STAGES);
    setNewStage("");
    setShowCols(true);
  };
  const toggleCol = (key) => {
    const setter = presetTab === "partner" ? setDraftPartnerCols : setDraftCols;
    setter((c) => c.includes(key) ? c.filter((x) => x !== key) : [...c, key]);
  };
  const addStage = () => { const v = newStage.trim(); if (v && !draftStages.includes(v)) { setDraftStages([...draftStages, v]); setNewStage(""); } };
  const removeStage = (s) => setDraftStages(draftStages.filter((x) => x !== s));
  const moveStage = (i, dir) => { const arr = [...draftStages]; const j = i + dir; if (j < 0 || j >= arr.length) return;[arr[i], arr[j]] = [arr[j], arr[i]]; setDraftStages(arr); };
  const moveCol = (i, dir) => {
    const setter = presetTab === "partner" ? setDraftPartnerCols : setDraftCols;
    setter((arr) => {
      const n = [...arr]; const j = i + dir;
      if (j < 0 || j >= n.length) return arr;
      [n[i], n[j]] = [n[j], n[i]];
      return n;
    });
  };
  const saveCols = async () => {
    const doc_types = draftDocs.split(",").map((s) => s.trim()).filter(Boolean);
    try {
      const { data } = await api.put("/settings/lead", { columns: draftCols, partner_columns: draftPartnerCols, doc_types, stages: draftStages });
      setSettings(data); setShowCols(false); toast.success("Lead view updated");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const allCfKeys = Object.keys(cfLabels);
  const activeDraft = presetTab === "partner" ? draftPartnerCols : draftCols;
  let rows = [...leads];
  if (pddMin) rows = rows.filter((l) => { const n = l.disbursed_at ? pddDays(l.disbursed_at) : null; return n !== null && n >= Number(pddMin); });
  if (sortPdd) rows = rows.sort((a, b) => ((b.disbursed_at ? pddDays(b.disbursed_at) : -1) - (a.disbursed_at ? pddDays(a.disbursed_at) : -1)));

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 font-heading tracking-tight">Leads</h1>
          <p className="text-sm text-slate-500 mt-1">Capture and manage customer loan enquiries</p>
        </div>
        <div className="flex gap-2">
          {isAdmin && <button data-testid="lead-columns-button" onClick={openCols} className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-800 font-medium text-sm flex items-center gap-2 border border-slate-200"><Columns3 size={16} /> Columns</button>}
          <button data-testid="add-new-lead-button" onClick={() => { setEditLead(null); setShowAdd(true); }} className={btnPrimary}><Plus size={16} /> New Lead</button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="lead-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or phone…" className={`${inputCls} pl-9`} />
        </div>
        <select data-testid="lead-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={`${inputCls} sm:w-48`}>
          <option value="">All Stages</option>
          {(settings.stages && settings.stages.length ? settings.stages : STAGES).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select data-testid="lead-type-filter" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className={`${inputCls} sm:w-48`}>
          <option value="">All Products</option>
          {products.filter((p) => p.active).map((t) => <option key={t.id} value={t.name}>{t.name}</option>)}
        </select>
        <select data-testid="lead-pdd-filter" value={pddMin} onChange={(e) => setPddMin(e.target.value)} className={`${inputCls} sm:w-40`}>
          <option value="">PDD: All</option>
          <option value="7">PDD ≥ 7d</option>
          <option value="15">PDD ≥ 15d</option>
          <option value="30">PDD ≥ 30d</option>
          <option value="60">PDD ≥ 60d</option>
        </select>
        <button type="button" data-testid="lead-pdd-sort" onClick={() => setSortPdd((v) => !v)} className={`px-3 py-2 rounded-lg text-sm font-medium border whitespace-nowrap ${sortPdd ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-600 border-slate-300"}`}>Oldest PDD first</button>
      </div>

      <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px]">
            <thead>
              <tr className="bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                {activeCols.map((k) => <th key={k} className={`px-4 py-3 ${cd(k).align === "right" ? "text-right" : "text-left"}`}>{cd(k).label}</th>)}
                {canManageLeads && <th className="text-right px-4 py-3">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((l) => (
                <tr key={l.id} data-testid={`lead-row-${l.id}`} className="border-b border-slate-100 hover:bg-slate-50/80 transition-colors text-sm text-slate-700">
                  {activeCols.map((k) => k === "lead_no" ? (
                    <td key={k} className="px-4 py-3">
                      <button data-testid={`lead-no-button-${l.id}`} onClick={() => setDetailLead(l)} className="font-mono text-xs font-semibold text-emerald-700 hover:text-emerald-900 hover:underline">{l.lead_no || "—"}</button>
                    </td>
                  ) : <td key={k} className={`px-4 py-3 ${cd(k).align === "right" ? "text-right" : ""}`}>{cd(k).cell(l)}</td>)}
                  {canManageLeads && (
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button data-testid={`view-lead-${l.id}`} onClick={() => setDetailLead(l)} className="p-2 rounded-lg text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 transition-colors"><Eye size={16} /></button>
                      {canEditLead && <button data-testid={`edit-lead-${l.id}`} onClick={() => { setEditLead(l); setShowAdd(true); }} className="p-2 rounded-lg text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 transition-colors"><Pencil size={16} /></button>}
                      {canEditLead && <button data-testid={`delete-lead-${l.id}`} onClick={() => del(l.id)} className="p-2 rounded-lg text-slate-500 hover:text-rose-600 hover:bg-rose-50 transition-colors"><Trash2 size={16} /></button>}
                    </div>
                  </td>
                  )}
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={activeCols.length + (canManageLeads ? 1 : 0)} className="px-4 py-12 text-center text-slate-400 text-sm">No leads found. Create your first lead.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <LeadFormModal open={showAdd} onClose={() => { setShowAdd(false); setEditLead(null); }} onCreated={load} editLead={editLead}
        products={products} lenders={lenders} partners={partners} docTypes={settings.doc_types || []} />

      <Modal open={showCols} onClose={() => setShowCols(false)} title="Lead View Settings" size="lg" testId="columns-modal">
        <div className="space-y-5">
          <div className="flex gap-2">
            {[["staff", "Staff / Dashboard"], ["partner", "Partner Portal"]].map(([k, lbl]) => (
              <button key={k} type="button" data-testid={`preset-tab-${k}`} onClick={() => setPresetTab(k)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${presetTab === k ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-600 border-slate-300"}`}>{lbl}</button>
            ))}
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Columns for {presetTab === "partner" ? "Partner Portal" : "Staff / Dashboard"}</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {Object.keys(BASE_COLS).map((k) => (
                <label key={k} className="flex items-center gap-2 text-sm text-slate-700 px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer">
                  <input data-testid={`col-toggle-${k}`} type="checkbox" checked={activeDraft.includes(k)} onChange={() => toggleCol(k)} className="w-4 h-4 accent-emerald-600" />
                  {BASE_COLS[k].label}
                </label>
              ))}
            </div>
          </div>
          {allCfKeys.length > 0 && (
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Custom field columns</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {allCfKeys.map((k) => (
                  <label key={k} className="flex items-center gap-2 text-sm text-slate-700 px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer">
                    <input data-testid={`col-toggle-${k}`} type="checkbox" checked={activeDraft.includes(k)} onChange={() => toggleCol(k)} className="w-4 h-4 accent-emerald-600" />
                    {cfLabels[k]}
                  </label>
                ))}
              </div>
            </div>
          )}

          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Column sequence — top row appears first in the table</div>
            <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
              {activeDraft.length === 0 && <div className="text-xs text-slate-400 italic px-1 py-2">Tick a column above to add it to the sequence.</div>}
              {activeDraft.map((k, i) => (
                <div key={k} data-testid={`col-seq-row-${k}`} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-slate-50">
                  <span className="text-[10px] text-slate-400 font-mono w-6">{i + 1}.</span>
                  <span className="flex-1 text-sm text-slate-700">{k.startsWith("cf:") ? (cfLabels[k] || k) : (cd(k)?.label || k)}</span>
                  <button type="button" data-testid={`col-up-${k}`} onClick={() => moveCol(i, -1)} className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30" disabled={i === 0}><ChevronUp size={14} /></button>
                  <button type="button" data-testid={`col-down-${k}`} onClick={() => moveCol(i, 1)} className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30" disabled={i === activeDraft.length - 1}><ChevronDown size={14} /></button>
                  <button type="button" onClick={() => toggleCol(k)} className="p-1 text-slate-400 hover:text-rose-600"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          </div>
          <div>
            <label className={labelCls}>Lead document types (comma separated)</label>
            <input data-testid="lead-doc-types-input" value={draftDocs} onChange={(e) => setDraftDocs(e.target.value)} className={inputCls} placeholder="PAN, Aadhaar, RC, Salary Slip" />
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Lead Statuses (sequence — leads move forward only)</div>
            <div className="space-y-2">
              {draftStages.map((s, i) => (
                <div key={s} data-testid={`stage-row-${i}`} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-slate-50">
                  <span className="flex-1 text-sm text-slate-700">{s}</span>
                  <button type="button" onClick={() => moveStage(i, -1)} className="p-1 text-slate-400 hover:text-slate-700"><ChevronUp size={14} /></button>
                  <button type="button" onClick={() => moveStage(i, 1)} className="p-1 text-slate-400 hover:text-slate-700"><ChevronDown size={14} /></button>
                  {CORE_STAGES.includes(s)
                    ? <span className="text-[10px] text-slate-400 w-12 text-right">core</span>
                    : <button type="button" data-testid={`remove-stage-${i}`} onClick={() => removeStage(s)} className="p-1 text-slate-400 hover:text-rose-600 w-12 flex justify-end"><Trash2 size={14} /></button>}
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-2">
              <input data-testid="new-stage-input" value={newStage} onChange={(e) => setNewStage(e.target.value)} className={inputCls} placeholder="Add a status (e.g. Sanctioned)" />
              <button type="button" data-testid="add-stage-button" onClick={addStage} className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-800 text-sm font-medium">Add</button>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2 border-t border-slate-100">
            <button onClick={() => setShowCols(false)} className="px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100 text-sm font-medium">Cancel</button>
            <button data-testid="save-columns-button" onClick={saveCols} className={btnPrimary}>Save</button>
          </div>
        </div>
      </Modal>

      {detailLead && (
        <LeadDetail lead={detailLead} lenders={lenders} partners={partners}
          onClose={() => setDetailLead(null)} onChange={() => { load(); }} docTypes={settings.doc_types || []} />
      )}
    </div>
  );
}
